import * as React from 'react';
import {useNumberInputContext} from '../_context';
import {
  NumberInputValueView,
  NumberInputValueWrapperView,
} from './NumberInputValue.styles';
import {SpringTooling} from '../../__SharedTypes';
import {NUMBER_INPUT_VARIANTS} from '../_constants';
import type {NumberInputVariants} from '../_types';
import {NumberInputPrefix} from './NumberInputPrefix';
import {NumberInputSuffix} from './NumberInputSuffix';

type NumberInputValueProps = {
  children?: React.ReactNode;
  variant?: NumberInputVariants;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> &
  SpringTooling & {
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void; //onChange is ordered after HTMLAttributes to resolve type merging;
  };

export const NumberInputValue = ({
  children,
  variant = NUMBER_INPUT_VARIANTS.DEFAULT,
  ...props
}: NumberInputValueProps) => {
  const {state, handlers} = useNumberInputContext();
  const isDefaultVariant = variant === NUMBER_INPUT_VARIANTS.DEFAULT;
  const hasPrefix = Boolean(state.prefix && isDefaultVariant);
  const hasSuffix = Boolean(state.suffix && isDefaultVariant);

  return (
    <NumberInputValueWrapperView
      data-disabled={state.isDisabled}
      data-error={state.isError}
      data-focused={state.isFocused}
      hasPrefix={hasPrefix}
      hasSuffix={hasSuffix}
      isDisabled={state.isDisabled}
      shouldShowTrailingControls={state.shouldShowTrailingControls}
      variant={variant}
    >
      {hasPrefix ? <NumberInputPrefix /> : null}
      <NumberInputValueView
        aria-valuemax={state.max}
        aria-valuemin={state.min}
        aria-valuenow={state._currentValue}
        autoComplete="off"
        autoCorrect="off"
        // allows undoing/redoing (in ShortcutHelper.ts) if the Input is focused
        data-wf-text-input
        disabled={state.isDisabled}
        inputMode="numeric"
        name={state.name}
        onBlur={handlers.onBlur}
        onChange={handlers.onChange}
        onFocus={handlers.onFocus}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) =>
          handlers.onKeyDown(event, event.shiftKey)
        }
        placeholder={state.placeholder}
        ref={state.ref}
        role="spinbutton"
        spellCheck="false"
        type="text"
        variant={variant}
        {...props}
      />
      {hasSuffix ? <NumberInputSuffix /> : null}

      {typeof children !== 'undefined' ? children : null}
    </NumberInputValueWrapperView>
  );
};
